/**
 * labels — the term map, and what happens when a key is not in it.
 *
 * UI-14. Two pages shipped with a source-code key printed where a word
 * belonged: the Suppliers list heading read `entity.supplier.plural` and the
 * Services list heading read `entity.service.plural`. Both keys were simply
 * never registered — but the reason nobody noticed for months is the line that
 * handled the miss:
 *
 *     return terms[termKey] ?? DEFAULT_TERMS[termKey] ?? String(termKey);
 *
 * The last branch turns "this label does not exist" into "print the
 * identifier". No test fails, no console warns, and the defect is visible only
 * to whoever opens the page. Worse, each call site had written its own
 * apparent safety net — `useTerm("entity.supplier.plural") || "Suppliers"` —
 * which never fired, because the raw key is a non-empty string. A reviewer
 * reading the call site saw a fallback that did not exist.
 *
 * So the mechanism changes before the two missing words are added:
 *
 *   - **In development and test, a missing key throws.** A label nobody
 *     registered is a bug, and a bug should fail the test that renders it.
 *   - **In production, a missing key is humanised** — `entity.supplier.plural`
 *     becomes "Suppliers". The worst a user can then see is an imperfect
 *     English word, never an identifier. Nothing a customer looks at should
 *     ever be a variable name.
 *
 * This file is deliberately free of JSX so the rules can be tested as
 * functions, not inferred from a rendered component.
 */

/**
 * Canonical term keys. Add to this list when new verticals introduce new
 * terms. Hardcoding raw labels in JSX is blocked by ESLint
 * (`ken/no-hardcoded-entity-labels`).
 */
export type TermKey =
  | "entity.customer.singular"
  | "entity.customer.plural"
  | "entity.customer.create_button"
  | "entity.customer.detail_title"
  | "entity.supplier.singular"
  | "entity.supplier.plural"
  | "entity.supplier.create_button"
  | "entity.supplier.detail_title"
  | "entity.service.singular"
  | "entity.service.plural"
  | "entity.service.create_button"
  | "entity.service.detail_title"
  | "entity.invoice.singular"
  | "entity.invoice.plural"
  | "entity.appointment.singular"
  | "entity.appointment.plural"
  | "entity.product.singular"
  | "entity.product.plural"
  | "entity.order.singular"
  | "entity.order.plural"
  | "entity.staff.singular"
  | "entity.staff.plural"
  | "entity.route.singular"
  | "entity.route.plural"
  | "entity.unit.singular"
  | "entity.unit.plural"
  | "entity.trailer.singular"
  | "entity.trailer.plural"
  | "entity.job.singular"
  | "entity.job.plural"
  | "entity.estimate.singular"
  | "entity.estimate.plural"
  | "entity.employee.singular"
  | "entity.employee.plural"
  | "entity.payee.singular"
  | "entity.class.singular"
  | "entity.class.plural"
  | "entity.approver.singular"
  | "dispatch.requested_by"
  | "dispatch.standby"
  | "customers.contact.signs_off"
  | "fleet.units_title"
  | "fleet.unit_or_trailer"
  | "fleet.unit_kind.truck"
  | "fleet.unit_kind.trailer"
  | "fleet.unit_kind.other"
  | "empty_state.no_customers"
  | "empty_state.no_invoices"
  | "empty_state.no_products"
  // Extension hatch — for very rare per-tenant keys not in the canonical list.
  | (string & {});

export type TermMap = Record<string, string>;

/**
 * The generic terms every vertical falls back to. `supplier` and `service`
 * carry the full four-key set the `customer` noun already models; the rest are
 * unchanged. Widening this to all twenty-three product nouns is UI-15's job —
 * this item exists to make that expansion safe, not to do it.
 */
export const DEFAULT_TERMS: TermMap = {
  "entity.customer.singular": "Customer",
  "entity.customer.plural": "Customers",
  "entity.customer.create_button": "Add customer",
  "entity.customer.detail_title": "Customer detail",
  "entity.supplier.singular": "Supplier",
  "entity.supplier.plural": "Suppliers",
  "entity.supplier.create_button": "Add supplier",
  "entity.supplier.detail_title": "Supplier detail",
  "entity.service.singular": "Service",
  "entity.service.plural": "Services",
  "entity.service.create_button": "Add service",
  "entity.service.detail_title": "Service detail",
  "entity.invoice.singular": "Invoice",
  "entity.invoice.plural": "Invoices",
  "entity.appointment.singular": "Appointment",
  "entity.appointment.plural": "Appointments",
  "entity.product.singular": "Product",
  "entity.product.plural": "Products",
  "entity.order.singular": "Order",
  "entity.order.plural": "Orders",
  "entity.staff.singular": "Staff member",
  "entity.staff.plural": "Staff",
  "entity.route.singular": "Route",
  "entity.route.plural": "Routes",
  // Setup standard Batch 7 (ADR-035): nouns an industry template may rename.
  // Generic nouns keep today's word; trucking-only words have a neutral
  // default and the trucking template supplies its own.
  "entity.unit.singular": "Unit",
  "entity.unit.plural": "Units",
  "entity.trailer.singular": "Trailer",
  "entity.trailer.plural": "Trailers",
  "entity.job.singular": "Job",
  "entity.job.plural": "Jobs",
  "entity.estimate.singular": "Estimate",
  "entity.estimate.plural": "Estimates",
  "entity.employee.singular": "Employee",
  "entity.employee.plural": "Employees",
  "entity.payee.singular": "Payee",
  "entity.class.singular": "Class",
  "entity.class.plural": "Classes",
  "entity.approver.singular": "Approver",
  "dispatch.requested_by": "Requested by",
  "dispatch.standby": "Waiting time",
  "customers.contact.signs_off": "Signs off work",
  "fleet.units_title": "Units and trailers",
  "fleet.unit_or_trailer": "unit or trailer",
  "fleet.unit_kind.truck": "Vehicle",
  "fleet.unit_kind.trailer": "Trailer",
  "fleet.unit_kind.other": "Other",
  "empty_state.no_customers": "No customers yet",
  "empty_state.no_invoices": "No invoices yet",
  "empty_state.no_products": "No products yet",
};

/** Thrown in development and test when a term key resolves to nothing. */
export class MissingTermError extends Error {
  readonly termKey: string;

  constructor(termKey: string) {
    super(
      `Unregistered terminology key "${termKey}". Add it to DEFAULT_TERMS in ` +
        `packages/terminology/src/labels.ts, or supply it from the tenant's ` +
        `terminology map. In production this would render "` +
        `${humaniseTermKey(termKey)}" — a guess, not a label anyone chose.`,
    );
    this.name = "MissingTermError";
    this.termKey = termKey;
  }
}

/** "customer" → "Customer"; leaves an already-capitalised word alone. */
function capitalise(word: string): string {
  return word.length === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * A deliberately naive English plural. It is a last resort shown only when a
 * key was never registered, so "imperfect but readable" is the whole bar —
 * getting `box → boxes` and `category → categories` right covers the nouns
 * this product actually has, and anything it gets wrong is still a word rather
 * than an identifier.
 */
function pluralise(word: string): string {
  if (word.length === 0) return word;
  if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
  return `${word}s`;
}

/**
 * Turn a key into the most readable English it can. The shape the product uses
 * is `<namespace>.<noun>.<facet>`, and the last two segments carry the meaning:
 *
 *   entity.supplier.plural        → "Suppliers"
 *   entity.supplier.singular      → "Supplier"
 *   entity.supplier.create_button → "Add supplier"
 *   entity.supplier.detail_title  → "Supplier detail"
 *   empty_state.no_customers      → "No customers"
 *
 * Anything that does not fit that shape falls through to "spell the words out"
 * — underscores and dots become spaces, and the first letter is capitalised.
 */
export function humaniseTermKey(termKey: string): string {
  const segments = String(termKey).split(".").filter(Boolean);
  if (segments.length === 0) return "";

  const facet = segments[segments.length - 1] ?? "";
  const noun = (segments[segments.length - 2] ?? "").replace(/_/g, " ");

  if (noun !== "") {
    switch (facet) {
      case "plural":
        return capitalise(pluralise(noun));
      case "singular":
        return capitalise(noun);
      case "create_button":
        return `Add ${noun}`;
      case "detail_title":
        return `${capitalise(noun)} detail`;
      default:
        break;
    }
  }

  // `empty_state.no_customers` and anything else: spell it out.
  const words = segments
    .slice(-2)
    .join(" ")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return capitalise(words === "" ? String(termKey) : words);
}

/**
 * Whether an unregistered key should throw.
 *
 * True everywhere except a production build. Vite (and Vitest) set
 * `import.meta.env`; a plain Node process sets `NODE_ENV`; when neither says
 * anything we assume production, because guessing "development" in an unknown
 * environment would mean throwing in front of a user.
 */
export function isStrictTermMode(): boolean {
  const viteEnv = (
    import.meta as unknown as {
      env?: { MODE?: string; PROD?: boolean; DEV?: boolean };
    }
  ).env;
  if (viteEnv) {
    if (viteEnv.PROD === true) return false;
    if (viteEnv.DEV === true) return true;
    if (typeof viteEnv.MODE === "string") return viteEnv.MODE !== "production";
  }
  const nodeEnv =
    typeof process !== "undefined" ? process.env?.["NODE_ENV"] : undefined;
  if (typeof nodeEnv === "string") return nodeEnv !== "production";
  return false;
}

/**
 * Resolve one key against a tenant's map, then the shared defaults.
 *
 * `strict` defaults to {@link isStrictTermMode}; pass it explicitly to assert
 * both behaviours from one test run.
 */
export function resolveTerm(
  terms: TermMap,
  termKey: TermKey,
  strict: boolean = isStrictTermMode(),
): string {
  // A blank word is no word: the shared default shows (SS-B7.6).
  const own = terms[termKey];
  const resolved = own !== undefined && own.trim() !== "" ? own : DEFAULT_TERMS[termKey];
  if (resolved !== undefined && resolved.trim() !== "") return resolved;
  if (strict) throw new MissingTermError(String(termKey));
  return humaniseTermKey(String(termKey));
}
